import React, { useEffect, useRef, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { motion } from 'framer-motion';

import BgEllipse from '@/components/shared/lp/BgEllipse';
import { BlocksMobileDesktop } from '@/components/shared/lp/komodo-wallet/desktop/BlocksMobileDesktop';
import { FAQ } from '@/components/shared/lp/komodo-wallet/desktop/FAQ';
import { JoinCommunityMobileDesktop } from '@/components/shared/lp/komodo-wallet/desktop/JoinCommunityMobileDesktop';
import { ListedCryptos500 } from '@/components/shared/lp/komodo-wallet/desktop/ListedCryptos500';
import { MobileWallet } from '@/components/shared/lp/komodo-wallet/desktop/MobileWallet';

import KomodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import android from '@/public/images/lp/komodo-wallet/android.svg';
import footerArrow from '@/public/images/lp/komodo-wallet/footer-arrow.svg';
import ios from '@/public/images/lp/komodo-wallet/ios.svg';
import androidqr from '@/public/images/lp/mobile/android-qr.png';
import heroDark from '@/public/images/lp/mobile/hero-dark.png';
import heroMobile from '@/public/images/lp/mobile/hero-mobile-3.png';
import iosqr from '@/public/images/lp/mobile/ios-qr.png';
import kmdLogoLight from '@/public/images/lp/mobile/kmd-logo-light.svg';

const VideoComponent = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  }, []);

  return (
    <video
      id='mobile-hero-vid'
      ref={videoRef}
      muted
      playsInline
      preload='true'
      className='mt-20 h-auto min-h-[490px] w-full xs:w-3/4 md:w-[300px] lg:mr-10 lg:mt-0 lg:w-[352px] xl:mr-16 xl:w-[400px] 2xl:mr-16 2xl:w-[430px] 3xl:mr-24 3xl:w-[470px]'
    >
      <source src='/img/mobile/vids/double_phone_alpha_all.webm' type='video/webm' />
      <source src='/img/mobile/vids/double_phone_alpha_for_safari1.mov' type='video/quicktime' />
    </video>
  );
};

export default function Mobile() {
  const [selected, setSelected] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const buttonHoverTap = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const downloadButtonWrapper = (children) => (
    <motion.div
      whileHover='hover'
      whileTap='tap'
      variants={buttonHoverTap}
      style={{ display: 'inline-block' }}
    >
      {children}
    </motion.div>
  );

  const mobileWalletData = [
    {
      header: 'Store your coins ',
      description:
        'Komodo Wallet is a secure place to HODL your crypto. The app is non-custodial. Only you have control of your private keys.',
    },
    {
      header: 'Trade on the DEX',
      description: (
        <span>
          Our decentralized exchange is powered by atomic swap technology.
          <br className='hidden lg:block 2xl:hidden' /> Trade cryptocurrencies across blockchain
          networks natively, without a middleman.
        </span>
      ),
    },
  ];
  const faqData = [
    {
      faqNo: '1',
      faqQstn: 'Besides mobile, is Komodo Wallet available on other platforms?',
      faqAnswer: (
        <span>
          Yes, Komodo Wallet is also available on a{' '}
          <Link href={siteData.webDEXlink} className='anchor-highlights'>
            web browser app
          </Link>{' '}
          and available to download on{' '}
          <Link href='/en/komodo-wallet/desktop/' className='anchor-highlights'>
            desktop
          </Link>{' '}
          (macOS, Windows, and Linux).
        </span>
      ),
    },
    {
      faqNo: '2',
      faqQstn: 'Who do I contact if I have a question/comment about Komodo mobile wallet?',
      faqAnswer: (
        <span>
          <Link href={siteData.social.discord} className='anchor-highlights'>
            Komodo&apos;s Discord Support channel
          </Link>{' '}
          is the best place to ask. Official team members have KMD logo badges in their usernames.
        </span>
      ),
    },
    {
      faqNo: '3',
      faqQstn: 'Do I need to keep the mobile app open when trading?',
      faqAnswer: (
        <span className='text-base font-medium sm:text-xl lg:text-lg'>
          Yes, atomic swaps require you to have a constant connection to the internet, so
          you&apos;ll need to have your Komodo Wallet mobile app open when trading. It&apos;s fine
          to keep your app running in the background and have other apps running at the same time.
        </span>
      ),
    },
    {
      faqNo: '4',
      faqQstn: 'Is the Komodo Wallet mobile app available in my region?',
      faqAnswer: (
        <span className='text-base font-medium sm:text-xl lg:text-lg'>
          Users in the following regions can&apos;t download the Komodo mobile wallet: United States
          of America, Canada, Hong Kong, Israel, Singapore, Sudan, Austria, and Iran. However, the
          Komodo desktop wallet is available worldwide.
        </span>
      ),
    },
  ];
  return (
    <>
      <div className='absolute w-full h-full overflow-hidden'>
        <div className='absolute mobile-header-ellipse-1'></div>
        <div className='absolute mobile-header-ellipse-2'></div>
      </div>
      <div className='absolute w-full h-full overflow-hidden'>
        <div className='absolute mobile-bottom-ellipse-1'></div>
        <div className='absolute mobile-bottom-ellipse-2'></div>
      </div>

      <main className='container relative mx-auto font-sans text-white md:grid md:grid-cols-24 md:gap-0 lg:px-8'>
        <BgEllipse className='-left-[30em] -top-[30em]' />
        <div className='hidden lg:col-span-2 lg:block'></div>
        <div className='relative px-4 xs:px-8 md:col-span-24 lg:col-span-20 lg:px-0'>
          <section className='relative z-20 flex flex-col items-center pt-32 lg:h-screen lg:max-h-[1080px] lg:min-h-[800px] lg:pt-0 xl:min-h-[810px] 2xl:min-h-[865px] 3xl:min-h-[920px]'>
            <motion.div
              variants={staggerChildren}
              initial='initial'
              animate='animate'
              className='flex flex-col items-center justify-between w-full md:h-2/3 lg:my-auto lg:flex-row'
            >
              <motion.div
                variants={fadeInUp}
                className='justify-start md:max-w-[75%] lg:w-[325px] xl:w-[407px] 2xl:w-[488px] 3xl:w-[611px]'
              >
                <motion.h1
                  variants={fadeInUp}
                  className='text-left text-2xl font-bold uppercase md:text-center lg:text-left lg:text-[2.5rem] lg:leading-[50px]'
                >
                  Download Komodo
                  <br className='lg:hidden' /> Mobile Wallet
                </motion.h1>
                <motion.p
                  variants={fadeInUp}
                  className='pt-4 text-sm font-medium text-left md:text-center md:text-base lg:text-left 2xl:pt-8 2xl:text-lg'
                >
                  Built by Komodo, Komodo Wallet is a non-custodial wallet and decentralized
                  exchange rolled into one app. Hold and trade hundreds of cryptocurrencies on your
                  mobile phone or tablet.
                </motion.p>
                <motion.div
                  variants={fadeInUp}
                  className='mx-auto flex flex-wrap items-center justify-between gap-[0.5px] pt-6 2xs:gap-5 xs:justify-start md:flex-nowrap md:justify-center md:gap-0 md:space-x-[12px] md:pt-4 lg:justify-start lg:space-x-[15px] xl:space-x-[30px] xl:px-0 xl:pt-6 2xl:space-x-[42px] 2xl:pt-10'
                >
                  <div className='group'>
                    <ExportedImage
                      placeholder='empty'
                      src={iosqr}
                      alt='ios qr'
                      className='absolute hidden h-auto w-[176.4px] -translate-x-[5%] -translate-y-[120%] lg:group-hover:block'
                    />
                    {downloadButtonWrapper(
                      <Link href={KomodoWalletDownloads.ios} className='all-mobile-hero-ios'>
                        <ExportedImage
                          placeholder='empty'
                          src={ios}
                          alt='ios'
                          className='h-auto w-[147px]'
                        />
                      </Link>,
                    )}
                  </div>
                  <div className='group'>
                    <ExportedImage
                      placeholder='empty'
                      src={androidqr}
                      alt='android qr'
                      className='absolute hidden h-auto w-[176.4px] -translate-x-[5%] -translate-y-[120%] lg:group-hover:block'
                    />
                    {downloadButtonWrapper(
                      <Link
                        href={KomodoWalletDownloads.android}
                        className='all-mobile-hero-android'
                      >
                        <ExportedImage
                          placeholder='empty'
                          src={android}
                          alt='android'
                          className='h-auto w-[147px]'
                        />
                      </Link>,
                    )}
                  </div>
                </motion.div>
                <motion.p
                  variants={fadeInUp}
                  className='hidden mt-4 text-xs font-medium lg:block xl:text-sm'
                >
                  Hover to show QR-code
                </motion.p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <VideoComponent />
              </motion.div>
            </motion.div>

            <Link href={siteData.komodoPlatformHome} className='relative bottom-[5%] lg:absolute'>
              <div className='flex flex-row items-center pt-20 mx-auto'>
                <p className='mr-3 text-sm font-medium'>Powered by</p>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <ExportedImage
                    placeholder='empty'
                    src={kmdLogoLight}
                    alt='komodo logo'
                    className='mr-3 h-auto w-[99.5px]'
                  />
                </motion.div>
              </div>
            </Link>
          </section>
          <section className='mx-auto pt-16 md:pb-[50px] md:pt-20 lg:pb-[100px] lg:pt-10 xl:pb-[150px] xl:pt-16 2xl:pt-20 3xl:pb-[200px] 3xl:pt-24'>
            <div className='mx-auto text-center'>
              <h2 className='text-2xl font-bold xs:text-3xl xl:text-4xl'>
                The Ultimate Mobile Wallet
              </h2>
              <p className='pt-4 mt-0 text-sm font-medium text-center md:-mt-5 xl:text-lg 2xl:pt-8'>
                The gold standard of DEX technology
              </p>
            </div>
            <div className='flex flex-col md:grid md:grid-cols-20 md:flex-row md:gap-0 3xl:pt-16'>
              <div className='md:col-span-11'>
                <ExportedImage
                  placeholder='empty'
                  src={heroMobile}
                  alt='mobile'
                  className='relative md:mt-32 md:h-auto md:w-full lg:mt-24 xl:mt-10 3xl:mt-0'
                />
              </div>
              <div className='mx-auto max-w-[394px] text-left md:col-span-9 md:mx-0 md:mt-12 lg:mt-24 2xl:mt-32 2xl:max-w-[494px] 3xl:mt-36'>
                {mobileWalletData.map((item, index) => (
                  <MobileWallet header={item.header} description={item.description} key={index} />
                ))}
              </div>
            </div>
          </section>

          <ListedCryptos500 />

          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='flex flex-col items-center pt-24 mx-auto text-center md:pt-12 lg:pb-24 xl:pb-36 3xl:pb-48'
          >
            <motion.h2
              variants={fadeInUp}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='text-2xl font-bold xs:text-3xl xl:text-4xl'
            >
              Begin your journey
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='mt-0 pt-4 text-center text-sm font-medium md:-mt-5 xl:text-xl xl:leading-[31px] 2xl:pt-8'
            >
              Download Komodo Mobile Wallet — financial
              <br /> freedom at your fingertips
            </motion.p>
            <motion.div
              variants={fadeInUp}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='flex w-full flex-wrap justify-between pt-12 xs:mx-auto xs:w-auto xs:justify-start xs:space-x-[24px] sm:items-center xl:flex-nowrap xl:space-x-[42px] xl:px-0'
            >
              {downloadButtonWrapper(
                <Link href={KomodoWalletDownloads.ios} className='all-mobile-begin-ios'>
                  <ExportedImage placeholder='empty' src={ios} alt='ios' className='h-auto' />
                </Link>,
              )}
              {downloadButtonWrapper(
                <Link href={KomodoWalletDownloads.android} className='all-mobile-begin-android'>
                  <ExportedImage
                    placeholder='empty'
                    src={android}
                    alt='android'
                    className='h-auto'
                  />
                </Link>,
              )}
            </motion.div>
          </motion.section>

          {/* <BlocksMobileDesktop />
          <JoinCommunityMobileDesktop /> */}

          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='relative py-12 mx-auto max-w-7xl sm:px-6 sm:py-16 md:pb-20 lg:px-8 lg:pb-28 3xl:pb-32'
          >
            <BgEllipse className='top-[300px] z-[-10]' />
            <motion.div
              variants={staggerChildren}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='mx-auto w-[280px] 2xs:w-[343px] sm:w-[400px] lg:w-[486px] 3xl:w-[586px]'
            >
              <motion.h2
                variants={fadeInUp}
                className='text-2xl font-bold text-center lg:pb-2 lg:text-3xl xl:pb-4 3xl:pb-8 3xl:text-4xl'
              >
                FAQs
              </motion.h2>
              {faqData.map((item, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <FAQ
                    faqNo={item.faqNo}
                    faqQstn={item.faqQstn}
                    faqAnswer={item.faqAnswer}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </motion.div>
              ))}
            </motion.div>
            <motion.div
              variants={fadeInUp}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='mx-auto w-[280px] pt-12 2xs:w-[343px] sm:w-[400px] lg:w-[486px] 3xl:w-[586px]'
            >
              <hr className='w-full' />
              <div className='flex flex-row items-center justify-between mt-4'>
                <p className='text-sm font-medium md:text-lg'>View user guides</p>
                {downloadButtonWrapper(
                  <Link href={siteData.mobileGuidesLink} rel='follow'>
                    <motion.button
                      type='button'
                      className='font-manrope cta-btn inline-flex h-[38px] w-[176px] items-center justify-center rounded-[14px] border border-none py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 2xs:w-[164px] sm:h-[50px] sm:w-[170px] sm:text-base'
                    >
                      <span className=''>Mobile Guides</span>
                      <ExportedImage
                        placeholder='empty'
                        src={footerArrow}
                        alt='footer-arrow'
                        className='ml-2 h-auto w-[10px]'
                      />
                    </motion.button>
                  </Link>,
                )}
              </div>
            </motion.div>
          </motion.section>
        </div>
        <div className='hidden lg:col-span-2 lg:block'></div>
      </main>
    </>
  );
}
